import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { setDemoClientId, isDemoMode } from "@/lib/demoMode";
import DashboardSidebar from "@/components/dashboard/DashboardSidebar";
import DashboardHome from "@/components/dashboard/DashboardHome";
import { toast } from "@/hooks/use-toast";
import RankingsTab from "@/components/dashboard/RankingsTab";
import CompetitorsTab from "@/components/dashboard/CompetitorsTab";
import SpeedTab from "@/components/dashboard/SpeedTab";
import BacklinksTab from "@/components/dashboard/BacklinksTab";
import ReportsTab from "@/components/dashboard/ReportsTab";
import ContentTab from "@/components/dashboard/ContentTab";
import IntegrationsTab from "@/components/dashboard/IntegrationsTab";
import WorkspaceTab from "@/components/dashboard/WorkspaceTab";
import SettingsTab from "@/components/dashboard/SettingsTab";
import logoMark from "@/assets/orbitrank-mark.png";
import BrandName from "@/components/BrandName";

interface DashboardProps {
  headerSlot?: React.ReactNode;
}

const Dashboard = ({ headerSlot }: DashboardProps = {}) => {
  const demoMode = isDemoMode();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [businessNameField, setBusinessNameField] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [businessNameInput, setBusinessNameInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<string>("Activate My Engine →");
  const navigate = useNavigate();

  const loadClients = async (uid: string) => {
    const { data: clients } = await supabase
      .from("clients")
      .select("id, name, business_name, website_url, positioning_completed_at, user_id")
      .eq("user_id", uid)
      .order("created_at", { ascending: true });

    // Defensive: only ever keep rows that actually belong to the signed-in user.
    const ownClients = (clients ?? []).filter((c) => c.user_id === uid);

    if (ownClients.length > 0) {
      setClientIds(ownClients.map((c) => c.id));
      setBusinessName(ownClients[0].name);
      setBusinessNameField((ownClients[0] as any).business_name ?? null);
      const clientsWithUrl = ownClients.filter((c) => c.website_url && c.website_url.trim() !== "");
      let hasUrl = clientsWithUrl.length > 0;

      // SECURITY: do NOT auto-apply localStorage `orbit_pending_website_url` to
      // this user's client row. That value can belong to a previous user on the
      // same browser and would silently overwrite the new user's website.
      // Always clear it on dashboard load so it can never leak across accounts.
      try { localStorage.removeItem("orbit_pending_website_url"); } catch {}
      try { localStorage.removeItem("orbit_pending_email"); } catch {}

      setNeedsOnboarding(!hasUrl);

      const positionedClients = ownClients.filter(
        (c) => c.website_url && c.website_url.trim() !== "" && !!c.positioning_completed_at
      );
      const anyPositioned = positionedClients.length > 0;
      if (hasUrl && !anyPositioned) {
        const seenIntro = sessionStorage.getItem("orbit_intro_seen") === "1";
        navigate(seenIntro ? "/onboarding" : "/onboarding-intro", { replace: true });
        return;
      }
    } else {
      setNeedsOnboarding(true);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (isDemoMode()) {
        // Demo mode — bypass auth, load the configured demo client directly.
        const { data: client } = await supabase
          .from("clients")
          .select("id, name, website_url")
          .eq("id", "383b3842-d83e-4ae2-ad28-d05c47ed3142")
          .maybeSingle();
        if (client) {
          setClientIds([client.id]);
          setBusinessName(client.name);
        }
        setNeedsOnboarding(false);
        setLoading(false);
        return;
      }
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session) {
        setUserId(session.user.id);
        await loadClients(session.user.id);
      } else {
        setNeedsOnboarding(true);
      }
      setLoading(false);
    };

    init();

    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      toast({
        title: "🎉 You're subscribed!",
        description: "Your subscription is active. Enjoy OrbitRank.",
      });
      params.delete("checkout");
      const newUrl = window.location.pathname + (params.toString() ? `?${params}` : "");
      window.history.replaceState({}, "", newUrl);
    }
  }, [navigate]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (typeof detail === "string") {
        setActiveTab(detail);
        setSidebarOpen(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };
    window.addEventListener("orbit:navigate-tab", handler);
    return () => window.removeEventListener("orbit:navigate-tab", handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setSidebarHidden(detail === "ads");
    };
    window.addEventListener("orbit:dashboard-view", handler);
    return () => window.removeEventListener("orbit:dashboard-view", handler);
  }, []);

  // Demo-only: auto-hide sidebar 2s after entering the demo dashboard
  useEffect(() => {
    if (!isDemoMode() || loading) return;
    const t = setTimeout(() => setSidebarHidden(true), 2000);
    return () => clearTimeout(t);
  }, [loading]);

  // Broadcast sidebar visibility so external chrome (e.g. demo banner) can match width
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("orbit:sidebar-hidden", { detail: sidebarHidden }));
  }, [sidebarHidden]);

  // Tag the body so global text rules can exclude the dashboard (Tailwind text-xs/sm/base
  // must keep their intended tight sizes here).
  useEffect(() => {
    document.body.classList.add("dashboard-shell");
    return () => document.body.classList.remove("dashboard-shell");
  }, []);

  // Lock body scroll when the mobile drawer is open (prevents Safari scroll-chain glitches)
  useEffect(() => {
    if (sidebarOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [sidebarOpen]);


  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSidebarOpen(false);
  };

  const normalizeUrl = (url: string) => {
    const trimmed = url.trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const buildDefaultOpportunities = (url: string) => {
    let domain = "website";

    try {
      domain = new URL(url).hostname.replace(/^www\./, "").split(".")[0] || domain;
    } catch {
      domain = url.replace(/^https?:\/\//i, "").replace(/^www\./, "").split(".")[0] || domain;
    }

    const base = domain.replace(/[-_]+/g, " ").trim() || "local business";
    const titleBase = base
      .split(" ")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");

    return [
      { title: `${titleBase} Services`, keyword: `${base} services` },
      { title: `${titleBase} Near Me`, keyword: `${base} near me` },
      { title: `Emergency ${titleBase}`, keyword: `emergency ${base}` },
    ];
  };

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    const url = normalizeUrl(websiteUrl);
    const businessName = businessNameInput.trim();
    if (!businessName) {
      toast({ title: "Please enter your business name", variant: "destructive" });
      return;
    }
    if (!url) {
      toast({ title: "Please enter a website URL", variant: "destructive" });
      return;
    }

    if (!userId) {
      setNeedsOnboarding(false);
      toast({ title: "🚀 Engine activated!", description: "Test mode — URL not saved." });
      return;
    }

    setSubmitting(true);
    setSubmitStatus("Saving your website…");
    try {
      const { data: existing } = await supabase
        .from("clients")
        .select("id")
        .eq("user_id", userId)
        .limit(1);

      let host = url;
      try {
        host = new URL(url).hostname.replace(/^www\./, "");
      } catch {}

      let clientId: string;
      if (existing && existing.length > 0) {
        clientId = existing[0].id;
        const { error } = await supabase
          .from("clients")
          .update({ website_url: url, name: businessName, business_name: businessName })
          .eq("id", clientId);
        if (error) throw error;
      } else {
        const { data: inserted, error } = await supabase
          .from("clients")
          .insert({ user_id: userId, name: businessName, business_name: businessName, website_url: url })
          .select("id")
          .single();
        if (error) throw error;
        clientId = inserted!.id;
      }

      setSubmitStatus("Analyzing your website…");
      console.log("[onboarding] Calling analyze-website", { client_id: clientId, url });
      const { data: analyzeData, error: analyzeError } = await supabase.functions.invoke(
        "analyze-website",
        { body: { url } }
      );
      console.log("[onboarding] analyze-website full response:", { data: analyzeData, error: analyzeError });
      if (analyzeError || !analyzeData?.success || !analyzeData?.analysis) {
        console.error("[onboarding] analyze-website failed:", analyzeError, analyzeData);
        throw new Error(analyzeError?.message || "Could not analyze your website. Please try again.");
      }
      console.log("[onboarding] analyze-website success:", analyzeData);

      const analysis = analyzeData.analysis;
      const opportunities = Array.isArray(analysis.opportunities) && analysis.opportunities.length > 0
        ? analysis.opportunities
        : buildDefaultOpportunities(url);

      if (!Array.isArray(analysis.opportunities) || analysis.opportunities.length === 0) {
        console.log("[onboarding] analyze-website returned empty opportunities, using defaults:", opportunities);
      }

      const { data: updatedClient, error: updateError } = await supabase
        .from("clients")
        .update({
          business_type: analysis.business_type ?? null,
          location: analysis.location ?? null,
          topics: analysis.main_topics ?? [],
          gaps: analysis.content_gaps ?? [],
          opportunities,
        })
        .eq("id", clientId)
        .select("id, business_type, location, opportunities")
        .single();
      if (updateError) {
        console.error("[onboarding] Failed to save analysis to client:", updateError);
        throw updateError;
      }
      console.log("[onboarding] client updated with analysis:", updatedClient);

      // Note: first-article generation is now triggered AFTER the user completes
      // the AI Positioning onboarding (see PositioningOnboarding.tsx) so every
      // article reflects the client's positioning narrative.
      console.log("[onboarding] Skipping generate-first-articles — will run after positioning is submitted.");

      await loadClients(userId);
      setNeedsOnboarding(false);
      toast({
        title: "🚀 Your engine is activated!",
        description: "First pages are being generated — check Content in a few minutes.",
      });
    } catch (err: any) {
      toast({ title: "Something went wrong", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
      setSubmitStatus("Activate My Engine →");
    }
  };

  if (loading) {
    return <div className="min-h-screen min-h-dvh bg-background" />;
  }

  return (
    <div
      className="flex min-h-screen min-h-dvh w-full bg-background text-foreground px-safe"
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      <div
        className="pointer-events-none fixed inset-0 z-0 hidden md:block"
        style={{
          background:
            "radial-gradient(ellipse at 15% 10%, hsl(var(--primary) / 0.035) 0%, transparent 55%), radial-gradient(ellipse at 85% 85%, hsl(var(--accent) / 0.025) 0%, transparent 55%)",
        }}
      />

      <div className="hidden md:block">
        <DashboardSidebar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          businessName={businessName}
          hidden={sidebarHidden}
          onToggleHidden={() => setSidebarHidden((h) => !h)}
        />
      </div>

      {/* Desktop floating reveal button — visible when sidebar is hidden */}
      <button
        type="button"
        aria-label="Show sidebar"
        onClick={() => setSidebarHidden(false)}
        className={`fixed left-4 top-4 z-[60] hidden h-10 w-10 items-center justify-center rounded-[10px] border border-border bg-card text-foreground shadow-[0_8px_24px_hsl(var(--foreground)/0.12)] transition-opacity duration-300 md:flex ${
          sidebarHidden ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      {/* Mobile drawer backdrop */}
      <div
        className={`fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm transition-opacity duration-300 md:hidden ${
          sidebarOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />

      {/* Mobile drawer */}
      <div
        className={`fixed left-0 top-0 z-[100] h-[100dvh] w-[82vw] max-w-[300px] pt-safe pb-safe transition-transform duration-300 ease-out md:hidden ${
          sidebarOpen ? "translate-x-0" : "pointer-events-none -translate-x-full"
        }`}
      >
        <DashboardSidebar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          businessName={businessName}
          mobile
        />
      </div>

      {/* Mobile sticky top bar — holds hamburger; sits above safe area */}
      <div
        className="fixed left-0 right-0 top-0 z-[95] flex items-center gap-3 px-4 pt-safe md:hidden"
        style={{
          background: "linear-gradient(180deg, hsl(var(--background) / 0.95), hsl(var(--background) / 0.75))",
          backdropFilter: "blur(14px) saturate(140%)",
          WebkitBackdropFilter: "blur(14px) saturate(140%)",
          borderBottom: "1px solid hsl(var(--border) / 0.6)",
        }}
      >
        <button
          type="button"
          aria-label="Toggle dashboard menu"
          onClick={() => setSidebarOpen((open) => !open)}
          className="my-2 flex h-11 w-11 items-center justify-center rounded-[12px] border border-primary/20 bg-card text-primary"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <BrandName className="text-[15px] font-[700] tracking-[-0.3px]" />
      </div>

      <main
        className={`relative z-[1] min-w-0 flex-1 ${demoMode ? "px-3 sm:px-4" : "px-4"} md:px-8 md:pb-7 md:pt-7 transition-[margin] duration-700 ease-in-out ${sidebarHidden ? "" : "md:ml-[236px]"}`}
        style={{
          paddingTop: "calc(env(safe-area-inset-top) + 68px)",
          paddingBottom: demoMode ? "max(8rem, env(safe-area-inset-bottom))" : "max(3rem, env(safe-area-inset-bottom))",
        }}
      >
        {headerSlot}
        {activeTab === "dashboard" && (
          <DashboardHome clientIds={clientIds} businessName={businessName} businessNameField={businessNameField} />
        )}
        {activeTab === "content" && <ContentTab clientIds={clientIds} />}
        {activeTab === "rankings" && <RankingsTab clientIds={clientIds} />}
        {activeTab === "integrations" && <IntegrationsTab clientIds={clientIds} />}
        {activeTab === "competitors" && <CompetitorsTab clientIds={clientIds} />}
        {activeTab === "speed" && <SpeedTab clientIds={clientIds} />}
        {activeTab === "backlinks" && <BacklinksTab clientIds={clientIds} />}
        {activeTab === "reports" && <ReportsTab clientIds={clientIds} />}
        {activeTab === "workspace" && <WorkspaceTab clientIds={clientIds} clientsResolved={!loading} />}
        {activeTab === "settings" && <SettingsTab clientIds={clientIds} />}
      </main>


      {needsOnboarding && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center px-5"
          style={{
            background: "hsl(var(--background) / 0.92)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
          }}
        >
          <div className="w-full max-w-[480px] rounded-[20px] border border-primary/20 bg-card p-7 shadow-[0_30px_80px_hsl(var(--foreground)/0.18)] md:p-9">
            <div className="mb-7 flex items-center justify-center gap-3">
              <img src={logoMark} alt="OrbitRank logo" className="h-8 w-8 object-contain" />
              <BrandName className="text-2xl font-semibold tracking-tight" />
            </div>
            <h1 className="mb-3 text-center text-[26px] font-[700] leading-[1.2] tracking-[-0.02em] text-foreground md:text-[30px]">
              Let's activate your SEO engine.
            </h1>
            <p className="mb-7 text-center text-[15px] text-muted-foreground" style={{ lineHeight: 1.55 }}>
              Tell us your business name and website — we'll start building your content immediately.
            </p>
            <form onSubmit={handleActivate} className="flex flex-col gap-4">
              <label className="flex flex-col gap-1.5 text-left">
                <span className="text-[13px] font-[600] text-foreground">What is the business name?</span>
                <input
                  type="text"
                  value={businessNameInput}
                  onChange={(e) => setBusinessNameInput(e.target.value)}
                  placeholder="e.g. Sandton Locksmith"
                  required
                  autoFocus
                  className="h-12 w-full rounded-[12px] border border-input bg-background px-4 text-[15px] text-foreground outline-none transition-colors placeholder:text-muted-foreground"
                />
              </label>
              <input
                type="text"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://yourwebsite.com"
                required
                className="h-12 w-full rounded-[12px] border border-input bg-background px-4 text-[15px] text-foreground outline-none transition-colors placeholder:text-muted-foreground"
              />
              <button
                type="submit"
                disabled={submitting}
                className="h-12 w-full rounded-[12px] bg-primary text-[15px] font-[700] text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-60"
              >
                {submitting ? submitStatus : "Activate My Engine →"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
